import { fileEtag, directoryEtag, forgetContentEtags } from './etag.js'
import { evaluateRange, buildRangeResponse } from './rangeRequests.js'
import { davTrace } from './log.js'

export class FileOperations {
    constructor(
        fileSystemHandler,
        mimeTypeDetector,
        directoryListing,
        lockManager
    ) {
        this.fileSystemHandler = fileSystemHandler
        this.mimeTypeDetector = mimeTypeDetector
        this.directoryListing = directoryListing
        this.lockManager = lockManager
    }

    async GET(path, { mimeType, requestHeaders, entryFilter, method = 'GET' } = {}) {
        if (!this.fileSystemHandler.rootHandle)
            throw { status: 503, message: 'No directory selected' }

        try {
            const fileHandle = await this.fileSystemHandler.getFileHandle(path)

            if (fileHandle.kind === 'directory') {
                const directoryListing =
                    await this.directoryListing.getDirectoryListingJSON(
                        path,
                        fileHandle,
                        this.lockManager,
                        entryFilter
                    )
                const json = JSON.stringify(directoryListing)
                return {
                    file: json,
                    mimeType: 'application/json; charset=utf-8',
                    headers: {
                        'X-Acequia-Directory-JSON': 'true',
                        ETag: `"${await directoryEtag(fileHandle)}"`,
                        'Content-Length': String(new TextEncoder().encode(json).length),
                    },
                }
            }

            const file = await fileHandle.getFile()
            mimeType =
                mimeType || this.mimeTypeDetector.detectMimeType(file.name)
            davTrace('mimetype: ', mimeType)

            const etag = await fileEtag(file, path)
            const validatorHeaders = {
                ETag: `"${etag}"`,
                'Last-Modified': new Date(file.lastModified).toUTCString(),
                'Content-Length': String(file.size),
            }

            // Range/206 — GET only (Nephele parity: HEAD ignores Range).
            // Throws {status: 400|416} on protocol errors; null means
            // fall through to a full-body 200.
            const reqHeaders = requestHeaders || {}
            if (method === 'GET') {
                const ranges = evaluateRange({
                    rangeHeader: reqHeaders['range'] ?? reqHeaders['Range'],
                    ifRange: reqHeaders['if-range'] ?? reqHeaders['If-Range'],
                    etag,
                    lastModified: file.lastModified,
                    size: file.size,
                })
                if (ranges) {
                    return buildRangeResponse(file, ranges, mimeType, validatorHeaders)
                }
            }

            switch (mimeType.split('/')[0]) {
                case 'text':
                case 'application':
                    if (
                        ['json', 'xml', 'javascript', 'plain', 'html'].some(
                            (subtype) => mimeType.endsWith(subtype)
                        )
                    ) {
                        return { file: await file.text(), mimeType, headers: validatorHeaders }
                    }
                    // Return the raw Blob — on WebRTC/same-device it ships as
                    // msgpack `bin` (1×); on the WS relay it rides a binary
                    // frame (1×) when the server advertises binaryFrames,
                    // base64 only as legacy fallback (binary-transfer #3).
                    // Was Array.from(Uint8Array) (~1.5× msgpack / ~3–4× as
                    // JSON int-array over WS). binary-transfer #2.
                    return { file, mimeType, headers: validatorHeaders }
                case 'image':
                case 'audio':
                case 'video':
                    return { file, mimeType, headers: validatorHeaders }
                default:
                    return { file, mimeType, headers: validatorHeaders }
            }
        } catch (error) {
            if (error.name === 'NotFoundError') {
                throw { status: 404, message: `Resource not found: ${path}` }
            } else if (error.name === 'TypeMismatchError') {
                throw {
                    status: 409,
                    message: `Resource type mismatch: ${path}`,
                }
            } else if (error.name === 'NotAllowedError') {
                throw { status: 403, message: `Access denied: ${path}` }
            } else if (error.status) {
                throw error
            } else {
                throw {
                    status: 500,
                    message: error.message || 'Internal server error',
                }
            }
        }
    }

    async PUT(path, content, mimeType, headers = {}) {
        if (!this.fileSystemHandler.rootHandle)
            throw { status: 503, message: 'No directory selected' }

        // Lock enforcement happens centrally in WebDAVFileSystem.handleRequest
        // (Phase 3): the If header is parsed once and every write method goes
        // through LockManager.checkWritable before dispatch.

        // Parity (Nephele / decision 1): 204 on overwrite, 201 on create, and
        // 409 when intermediate collections are missing — never silently
        // auto-create the parent tree.
        let exists = false
        try {
            const existing = await this.fileSystemHandler.getFileHandle(path)
            if (existing.kind === 'directory') {
                throw { status: 409, message: `Cannot PUT to a collection: ${path}` }
            }
            exists = true
        } catch (error) {
            if (error.status) throw error
            if (error.name === 'TypeMismatchError') {
                // an intermediate path segment is a file, not a collection
                throw { status: 409, message: `Resource tree not complete: ${path}` }
            }
            if (error.name !== 'NotFoundError') {
                throw { status: 500, message: error.message || 'Internal server error' }
            }
        }
        if (!exists) {
            const parentPath = path.split('/').slice(0, -1).join('/')
            try {
                await this.fileSystemHandler.getFileHandle(parentPath, { getDirectory: true })
            } catch (error) {
                throw { status: 409, message: `Parent collection does not exist: ${path}` }
            }
        }

        // A PUT that creates the file and then fails must not leave an empty
        // file at the path (large-uploads project, finding 3, 2026-10-15: a
        // 250 MB upload cut off by Cloudflare's 413 left 0 bytes there).
        let createdHandle = null
        forgetContentEtags(path) // the bytes at this path are about to change
        try {
            const fileHandle = await this.fileSystemHandler.getFileHandle(
                path,
                { create: true }
            )
            if (!exists) createdHandle = fileHandle
            const writable = await fileHandle.createWritable()

            mimeType = mimeType || this.mimeTypeDetector.detectMimeType(path)
            davTrace('putting content: ', content)

            if (typeof ReadableStream !== 'undefined' && content instanceof ReadableStream) {
                // Streamed PUT (browserdav-transport-robustness): chunks go
                // to disk as they arrive — the file is never held in memory.
                // On failure abort so the partial write is discarded, not
                // committed. (Explicit reader loop rather than pipeTo: the
                // FSA writable's convenience write() is all we need, and it
                // keeps the path testable against non-WritableStream shims.)
                const reader = content.getReader()
                try {
                    for (;;) {
                        const { done, value } = await reader.read()
                        if (done) break
                        await writable.write(value)
                    }
                    await writable.close()
                } catch (error) {
                    try { await writable.abort() } catch { /* already closed */ }
                    throw { status: 500, message: `Streamed write failed: ${error?.message || error}` }
                }
                return { status: exists ? 204 : 201 }
            }

            if (content instanceof Blob) {
                await writable.write(content)
            } else if (content instanceof ArrayBuffer) {
                await writable.write(new Uint8Array(content))
            } else if (ArrayBuffer.isView(content)) {
                // Handle TypedArrays (Uint8Array, etc.)
                await writable.write(content)
            } else if (Array.isArray(content)) {
                // Handle plain arrays (from JSON serialization of byte arrays)
                await writable.write(new Uint8Array(content))
            } else if (typeof content === 'string') {
                await writable.write(content)
            } else if (content && typeof content === 'object' && content.type === 'Buffer' && Array.isArray(content.data)) {
                // Handle Node.js Buffer-like objects that got JSON serialized
                await writable.write(new Uint8Array(content.data))
            } else if (content == null) {
                // Empty PUT (no body) — valid WebDAV; create/truncate to a
                // zero-byte file like nephele. Live failure: TPC COPY of a
                // 0-byte file 415'd here because null matched no branch.
            } else {
                await writable.abort()
                console.error('Unsupported content type:', typeof content, content)
                throw { status: 415, message: 'Unsupported content type' }
            }

            await writable.close()
            return { status: exists ? 204 : 201 }
        } catch (error) {
            if (createdHandle) await this._discardCreatedFile(path, createdHandle)
            if (error.name === 'NotFoundError') {
                throw {
                    status: 404,
                    message: `Parent directory not found for: ${path}`,
                }
            } else if (error.name === 'TypeMismatchError') {
                throw {
                    status: 409,
                    message: `Resource type mismatch: ${path}`,
                }
            } else if (error.name === 'NotAllowedError') {
                throw { status: 403, message: `Access denied: ${path}` }
            } else if (error.name === 'QuotaExceededError') {
                throw { status: 507, message: 'Insufficient storage' }
            } else if (error.status) {
                throw error
            } else {
                throw {
                    status: 500,
                    message: error.message || 'Internal server error',
                }
            }
        }
    }

    /**
     * Remove the entry a failed PUT created. Only while it is still empty: a
     * writable commits on close (write-then-swap), so an empty entry is ours,
     * and a concurrent PUT that committed content in the meantime is left
     * alone. Best effort; the PUT's own error is what the caller sees.
     */
    async _discardCreatedFile(path, fileHandle) {
        try {
            if ((await fileHandle.getFile()).size !== 0) return
            const parentHandle = await this.fileSystemHandler.getFileHandle(
                path.split('/').slice(0, -1).join('/'),
                { getDirectory: true }
            )
            await parentHandle.removeEntry(path.split('/').pop())
        } catch {
            /* already gone, or not removable: nothing more to do */
        }
    }

    async MKCOL(path, headers = {}) {
        if (!this.fileSystemHandler.rootHandle)
            throw { status: 503, message: 'No directory selected' }

        // A collection URL may end in a slash (RFC 4918 treats `a/` and `a`
        // as the same collection; Nephele accepts either). The slashed form
        // used to split into a parent of itself — `a/` → parent `a`, not yet
        // there → 409 — so every client that names a new collection the
        // usual way failed here (browserdav-mkcol-rejects-the-trailing-slash-
        // collection-url, 2026-09-25).
        path = path.replace(/\/+$/, '')

        // Parity (RFC 4918 §9.3 / Nephele): 405 when anything already exists
        // at the target, 409 when the parent collection is missing — never
        // silently auto-create intermediate collections.
        try {
            await this.fileSystemHandler.getFileHandle(path)
            throw { status: 405, message: `Resource already exists: ${path}` }
        } catch (error) {
            if (error.status) throw error
            if (error.name === 'TypeMismatchError') {
                // an intermediate path segment is a file, not a collection
                throw { status: 409, message: `Parent collection does not exist: ${path}` }
            }
            if (error.name !== 'NotFoundError') {
                throw { status: 500, message: error.message || 'Internal server error' }
            }
        }
        const parentPath = path.split('/').slice(0, -1).join('/')
        try {
            await this.fileSystemHandler.getFileHandle(parentPath, { getDirectory: true })
        } catch (error) {
            throw { status: 409, message: `Parent collection does not exist: ${path}` }
        }

        try {
            await this.fileSystemHandler.getFileHandle(path, {
                create: true,
                directory: true,
            })
        } catch (error) {
            if (error.name === 'NotAllowedError') {
                throw { status: 403, message: `Access denied: ${path}` }
            } else if (error.status) {
                throw error
            } else {
                throw {
                    status: 500,
                    message: error.message || 'Internal server error',
                }
            }
        }
    }

    async DELETE(path, headers = {}) {
        if (!this.fileSystemHandler.rootHandle)
            throw { status: 503, message: 'No directory selected' }

        // `col/` and `col` are the same collection (as for MKCOL above): the
        // slashed form split to an empty name and removeEntry('') threw 500.
        path = path.replace(/\/+$/, '')
        try {
            const parentPath = path.split('/').slice(0, -1).join('/')
            const name = path.split('/').pop()
            const parentHandle = await this.fileSystemHandler.getFileHandle(
                parentPath,
                {
                    getDirectory: true,
                }
            )
            await parentHandle.removeEntry(name, { recursive: true })
            forgetContentEtags(path)

            this.lockManager.removeLocksForPath(path)
        } catch (error) {
            if (error.name === 'NotFoundError') {
                throw { status: 404, message: `Resource not found: ${path}` }
            } else if (error.name === 'InvalidModificationError') {
                throw {
                    status: 423,
                    message: `Resource is locked or in use: ${path}`,
                }
            } else if (error.name === 'NotAllowedError') {
                throw { status: 403, message: `Access denied: ${path}` }
            } else if (error.status) {
                throw error
            } else {
                throw {
                    status: 500,
                    message: error.message || 'Internal server error',
                }
            }
        }
    }

    async _resolveOptional(path) {
        try {
            return await this.fileSystemHandler.getFileHandle(path)
        } catch {
            return null
        }
    }

    /** True when a file or collection exists at the share-relative path. */
    async exists(path) {
        return (await this._resolveOptional(path)) !== null
    }

    async _copyEntry(srcHandle, destParentHandle, destName, depth) {
        if (srcHandle.kind === 'file') {
            const file = await srcHandle.getFile()
            const destHandle = await destParentHandle.getFileHandle(destName, { create: true })
            const writable = await destHandle.createWritable()
            await writable.write(file)
            await writable.close()
            return
        }
        const destDir = await destParentHandle.getDirectoryHandle(destName, { create: true })
        if (depth > 0) {
            for await (const entry of srcHandle.values()) {
                await this._copyEntry(entry, destDir, entry.name, depth - 1)
            }
        }
    }

    /**
     * COPY / MOVE shared plumbing (browserdav-nephele-parity Phase 3).
     * Returns { status: 201 (created) | 204 (overwrote) }.
     * MOVE prefers the File System Access move() where the platform provides
     * it (Chromium, files, same-directory-tree); otherwise copy + delete.
     * That fallback is NOT atomic like Nephele's fsp.rename() — claim-by-MOVE
     * patterns should keep using server origins (documented in the parity
     * project; substrate constraint, not a bug).
     */
    async _copyOrMove(srcPath, destPath, { overwrite = true, depth = Infinity, move = false } = {}) {
        if (!this.fileSystemHandler.rootHandle)
            throw { status: 503, message: 'No directory selected' }

        if (srcPath === destPath) {
            throw { status: 403, message: 'Source and destination are the same resource' }
        }
        if (destPath.startsWith(srcPath + '/')) {
            throw { status: 409, message: 'Destination cannot be inside the source' }
        }

        let srcHandle
        try {
            srcHandle = await this.fileSystemHandler.getFileHandle(srcPath)
        } catch (error) {
            if (error.name === 'NotFoundError') throw { status: 404, message: `Resource not found: ${srcPath}` }
            throw { status: 500, message: error.message || 'Internal server error' }
        }

        const destExisting = await this._resolveOptional(destPath)
        if (destExisting && !overwrite) {
            throw { status: 412, message: `Destination exists and Overwrite is F: ${destPath}` }
        }

        const destParentPath = destPath.split('/').slice(0, -1).join('/')
        const destName = destPath.split('/').pop()
        let destParent
        try {
            destParent = await this.fileSystemHandler.getFileHandle(destParentPath, { getDirectory: true })
        } catch {
            throw { status: 409, message: `Destination parent collection does not exist: ${destPath}` }
        }

        if (destExisting) {
            await destParent.removeEntry(destName, { recursive: true })
        }
        forgetContentEtags(destPath)
        if (move) forgetContentEtags(srcPath)

        try {
            if (move && srcHandle.kind === 'file' && typeof srcHandle.move === 'function') {
                await srcHandle.move(destParent, destName)
            } else {
                await this._copyEntry(srcHandle, destParent, destName, move ? Infinity : depth)
                if (move) {
                    const srcParentPath = srcPath.split('/').slice(0, -1).join('/')
                    const srcParent = await this.fileSystemHandler.getFileHandle(srcParentPath, { getDirectory: true })
                    await srcParent.removeEntry(srcHandle.name, { recursive: true })
                }
            }
        } catch (error) {
            if (error.name === 'NotAllowedError') throw { status: 403, message: `Access denied: ${destPath}` }
            if (error.name === 'QuotaExceededError') throw { status: 507, message: 'Insufficient storage' }
            if (error.status) throw error
            throw { status: 500, message: error.message || 'Internal server error' }
        }

        return { status: destExisting ? 204 : 201 }
    }

    async COPY(srcPath, destPath, options = {}) {
        srcPath = srcPath.replace(/\/+$/, '') || srcPath
        return this._copyOrMove(srcPath, destPath, { ...options, move: false })
    }

    async MOVE(srcPath, destPath, options = {}) {
        srcPath = srcPath.replace(/\/+$/, '') || srcPath
        return this._copyOrMove(srcPath, destPath, { ...options, move: true })
    }
}
